import { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
    faDatabase,
    faChartBar,
    faTable,
    faProjectDiagram,
    faCode,
    faColumns,
    faSearch,
    faRotate,
    faChevronLeft,
    faChevronRight,
    faPlug,
    faMagnifyingGlassChart,
    faCheck,
    faXmark,
    faLayerGroup,
    faFileExcel,
    faObjectGroup,
    faUserGroup,
    faSquareRootVariable,
    faTags
} from '@fortawesome/free-solid-svg-icons';

import { discoveryApi } from '../services/api';
import { exportWorkbook, summaryRowsFromObject } from '../utils/exportToExcel';

import '../assets/discovery.css';

export default function Discovery() {

    const [activeTab, setActiveTab] = useState('datasets');

    const [search, setSearch] = useState('');

    const [page, setPage] = useState(1);

    const [showConnectModal, setShowConnectModal] =
        useState(false);

    const [selectedPages, setSelectedPages] =
        useState([]);

    const [analyzing, setAnalyzing] =
        useState(false);

    const [selectedResource, setSelectedResource] =
        useState(null);

    const [analysis, setAnalysis] =
        useState(null);

    // True once the user has explicitly picked pages and run
    // POST /discovery/analyze themselves — while true, the auto-seed
    // effect stops overwriting `analysis` with the org-wide GET /discovery
    // result on every refresh.
    const [isManualAnalysis, setIsManualAnalysis] =
        useState(false);

    const [selectedResources, setSelectedResources] =
        useState([]);

    const [filterPage, setFilterPage] =
        useState('all');

    const [detailPage, setDetailPage] =
        useState(null);

    const pageSize = 10;


    /*
    |--------------------------------------------------------------------------
    | Discovery API
    |--------------------------------------------------------------------------
    */

    const {
        data: discoveryResponse,
        isLoading,
        isFetching,
        refetch
    } = useQuery(
        'domo-discovery',
        () => discoveryApi.discovery(),
        {
            // Trigger GET /discovery automatically as soon as the page
            // loads, instead of waiting for the user to open the Connect
            // modal — the returned discovery data (available pages, etc.)
            // is needed right away for the page picker.
            enabled: true,
            refetchOnWindowFocus: false,
            staleTime: 30000
        }
    );


    /*
    |--------------------------------------------------------------------------
    | Discovery Data
    |--------------------------------------------------------------------------
    | GET /discovery responds with:
    |   {
    |     success: true,
    |     data: {
    |       pages: [...],            <- available pages for the picker modal
    |       summary: { pages: N },   <- just a page count, NOT the full
    |                                    datasets/cards/columns/etc summary
    |       discovery: { ... } | null  <- the latest stored discovery/
    |                                      analyze/assess payload, shaped
    |                                      like a normal analysis result
    |                                      (summary, datasets, cards, ...).
    |                                      null if nothing's been run yet.
    |     },
    |     meta: { discovery: { id, source, createdAt } }
    |   }
    */
    const discoveryData =
        discoveryResponse?.data?.data || {};

    // The nested, already-analyzed payload (or null if none exists yet) —
    // this is what actually has datasets/cards/columns/summary counts, and
    // is what gets shown as `analysis` below.
    const storedDiscoveryResult = discoveryData.discovery || null;


    /*
    |--------------------------------------------------------------------------
    | Available Pages / Dashboards
    |--------------------------------------------------------------------------
    */

    const availablePages =
        Array.isArray(discoveryData.pages)
            ? discoveryData.pages
            : [];


    /*
    |--------------------------------------------------------------------------
    | Show discovery data as soon as it loads (and keep it in sync on Refresh)
    |--------------------------------------------------------------------------
    | GET /discovery's `data.discovery` field already holds the latest
    | stored analyze/assess result. Previously the response was only used
    | to feed the "available pages" picker in the Connect modal, and the
    | whole summary/tabs UI stayed empty until the user manually picked
    | pages and ran the separate POST /discovery/analyze — so this
    | already-available data never actually rendered.
    |
    | This effect keeps `analysis` synced from `data.discovery` every time a
    | fresh discovery response comes in — including from clicking Refresh —
    | as long as the user hasn't manually picked specific pages and run
    | POST /discovery/analyze themselves (isManualAnalysis). Once they do
    | that, their scoped result takes over and auto-seeding stops
    | overwriting it, until they clear that selection.
    */
    useEffect(() => {
        if (isManualAnalysis) return;
        if (!discoveryResponse) return;
        if (!storedDiscoveryResult) return;

        setAnalysis(storedDiscoveryResult);
    }, [discoveryResponse, isManualAnalysis, storedDiscoveryResult]);


    /*
    |--------------------------------------------------------------------------
    | Current Data
    |--------------------------------------------------------------------------
    */

    const currentData = useMemo(() => {
        const raw = analysis?.[activeTab] || [];
        return raw;
    }, [analysis, activeTab]);


    /*
    |--------------------------------------------------------------------------
    | Page-wise Summary (main table after analysis)
    |--------------------------------------------------------------------------
    */

    const pageWiseSummary = useMemo(() => {
        if (!analysis) return [];

        // Prefer the pages that came back with the analysis itself
        // (analysis.pages) — this is what's populated when discovery data
        // is auto-loaded on page load. Fall back to whatever pages the
        // user explicitly selected in the Connect modal, for the manual
        // "pick pages -> Analyze" flow.
        const pages = (analysis.pages?.length ? analysis.pages : selectedPages) || [];
        if (!pages.length) return [];

        const cards = analysis.cards || [];
        const datasets = analysis.datasets || [];
        const columns = analysis.columns || [];
        const beastmodes = analysis.beastmodes || [];
        const dataflows = analysis.dataflows || [];

        return pages.map(pg => {
            const pgId = String(pg.id || pg.pageId || '');
            const pgName = pg.name || pg.pageName || pg.title || `Page ${pgId}`;

            const pgCards = cards.filter(c =>
                String(c.pageId || c.page_id || c.page || '') === pgId
            );

            const pgDatasetIds = new Set(
                pgCards.map(c =>
                    String(c.datasetId || c.dataSetId || c.datasourceId || c.detail?.datasourceId || c.detail?.dataSetId || '')
                ).filter(Boolean)
            );

            const pgCardIds = new Set(
                pgCards.map(c =>
                    String(c.id || c.urn)
                ).filter(Boolean)
            );

            const pgDatasets = datasets.filter(d =>
                pgDatasetIds.has(String(d.id || d.datasetId || ''))
            );

            const pgColumns = columns.filter(c =>
                pgDatasetIds.has(String(c.datasetId || ''))
            );

            const pgBeastmodes = beastmodes.filter(b =>
                pgDatasetIds.has(String(b.datasetId || b.dataset || ''))
            );
            
            const pgFilters = (analysis?.filters || []).filter(f =>
                String(f.pageId || '') === pgId
            );

            const pgDataflows = dataflows.filter(df => {
                const inputs = df.inputs || df.inputDatasets || [];
                const outputs = df.outputs || df.outputDatasets || [];
                const all = [...inputs, ...outputs, df.datasetId].filter(Boolean).map(String);
                return all.some(id => pgDatasetIds.has(id));
            });

            // Calculated fields / variables / sets / groups / hierarchy
            // come directly from each card (card.calc, card.variables, etc.)
            // enriched by the backend.
            const pgCalc = pgCards.flatMap(c => c.calc || []);
            const pgVariables = pgCards.flatMap(c => c.variables || []);
            const pgSets = pgCards.flatMap(c => c.sets || []);
            const pgGroups = pgCards.flatMap(c => c.groups || []);
            const pgHierarchy = pgCards.flatMap(c => c.hierarchy || []);

            return {
                id: pgId,
                name: pgName,
                page: pg,
                cards: pgCards.length,
                datasets: pgDatasets.length,
                columns: pgColumns.length,
                filters: pgFilters.length,
                dataflows: pgDataflows.length,
                calc: pgCalc.length,
                variables: pgVariables.length,
                sets: pgSets.length,
                groups: pgGroups.length,
                hierarchy: pgHierarchy.length,
            };
        });
    }, [analysis, selectedPages]);

    console.log(pageWiseSummary)
    /*
    |--------------------------------------------------------------------------
    | Search
    |--------------------------------------------------------------------------
    */

    const filteredData = useMemo(() => {

        if (!search.trim()) {
            return currentData;
        }

        const searchValue =
            search.toLowerCase();

        return currentData.filter((item) => {

            return JSON.stringify(item)
                .toLowerCase()
                .includes(searchValue);

        });

    }, [currentData, search]);


    /*
    |--------------------------------------------------------------------------
    | Pagination
    |--------------------------------------------------------------------------
    */

    const totalPages = Math.max(
        1,
        Math.ceil(
            filteredData.length / pageSize
        )
    );


    const paginatedData = useMemo(() => {

        const start =
            (page - 1) * pageSize;

        return filteredData.slice(
            start,
            start + pageSize
        );

    }, [filteredData, page]);


    /*
    |--------------------------------------------------------------------------
    | Open Connect Modal
    |--------------------------------------------------------------------------
    */

    const handleConnectDomo = async () => {

        setShowConnectModal(true);

        /*
         * Load Domo resources only when
         * user opens the selection modal.
         */

        if (!discoveryResponse) {
            await refetch();
        }
    };


    /*
    |--------------------------------------------------------------------------
    | Select / Unselect Page
    |--------------------------------------------------------------------------
    */

    const togglePageSelection = (pageItem) => {

        const exists =
            selectedPages.some(
                item =>
                    String(item.id) ===
                    String(pageItem.id)
            );

        const nextSelectedPages = exists
            ? selectedPages.filter(
                item =>
                    String(item.id) !==
                    String(pageItem.id)
            )
            : [...selectedPages, pageItem];

        setSelectedPages(nextSelectedPages);
        setSelectedResources(nextSelectedPages);
        setSelectedResource(nextSelectedPages.length ? nextSelectedPages : null);
    };


    /*
    |--------------------------------------------------------------------------
    | Analyze Selected Resource
    |--------------------------------------------------------------------------
    */

    const handleAnalyzeSelected = async () => {
        const resourcesToAnalyze =
            selectedResources.length
                ? selectedResources
                : selectedPages;

        

        if (!resourcesToAnalyze.length) {
            return;
        }

        try {

            setAnalyzing(true);
            setSelectedResources(resourcesToAnalyze);
            setSelectedResource(resourcesToAnalyze);

            const response =
                await discoveryApi.analyze({
                    resourceType: 'page',
                    resourceIds:
                        resourcesToAnalyze
                            .map(item => item.id)
                });

            setAnalysis(
                response?.data?.data ||
                response?.data ||
                {}
            );

            setIsManualAnalysis(true);
            setShowConnectModal(false);

        } catch (error) {

            console.error(
                'Domo analysis failed:',
                error
            );

            alert(
                error?.response?.data?.message ||
                'Unable to analyze the selected Domo resource.'
            );

        } finally {

            setAnalyzing(false);

        }

    };


    /*
    |--------------------------------------------------------------------------
    | Refresh
    |--------------------------------------------------------------------------
    */

    const handleRefresh = async () => {
        setPage(1);
        setSearch('');
        await refetch();
    };


    /*
    |--------------------------------------------------------------------------
    | Export To Excel
    |--------------------------------------------------------------------------
    */

    const handleExportExcel = () => {

        if (!analysis) {
            return;
        }

        const summaryRows = [
            ...summaryRowsFromObject(analysis.summary, {
                datasets: 'Datasets',
                cards: 'Cards',
                columns: 'Columns',
                pages: 'Pages',
                dataflows: 'DataFlows',
                filters: 'Filters',
                calc: 'Calculated Fields',
                variables: 'Variables',
                sets: 'Sets',
                groups_: 'Groups',
                hierarchy: 'Hierarchy'
            }),
            { Metric: '', Value: '' },
            { Metric: 'Selected Pages', Value: selectedPages.map(p => p.name || p.title).join(', ') }
        ];

        const pageWiseRows = pageWiseSummary.map(row => ({
            'Page Name': row.name,
            Datasets: row.datasets,
            Cards: row.cards,
            Columns: row.columns,
            Filters: row.filters,
            'Calculated Fields': row.calc,
            Variables: row.variables,
            Sets: row.sets,
            Groups: row.groups,
            Hierarchy: row.hierarchy,
            DataFlows: row.dataflows
        }));

        const datasetRows = (analysis.datasets || []).map(item => ({
            Name: item.name || item.datasetName || item.displayName || '-',
            ID: item.id || item.datasetId || '-',
            Columns: Array.isArray(item.schema) ? item.schema.length : (item.columns?.length || item.columnCount || item.schema?.columns?.length || 0),
            Rows: item.rows || item.rowCount || item.dataRows || 0
        }));

        const cardRows = (analysis.cards || []).map(item => ({
            Name: item.name || item.title || '-',
            Type: item.type || '-',
            'Chart Type': item.chartType || item.detail?.chartType || '-',
            'Dataset ID': item.datasetId || item.dataSetId || item.detail?.datasourceId || '-',
            Page: item.pageName || '-'
        }));

        const columnRows = (analysis.columns || []).map(item => ({
            Dataset: item.datasetName || item.datasetId || '-',
            Column: item.name || item.columnName || '-',
            'Data Type': item.type || item.dataType || item.columnType || '-'
        }));

        const filterRows = (analysis.filters || []).map(item => ({
            Column: item.column || '-',
            Operator: item.operator || item.operand || '-',
            Values: Array.isArray(item.values) ? item.values.join(', ') : (item.values || '-'),
            Card: item.cardName || '-',
            Page: item.pageName || '-'
        }));

        const calcRows = (analysis.cards || []).flatMap(c => (c.calc || []).map(item => ({
            Card: c.name || c.title || '-',
            Name: item.name || '-',
            Type: item.dataType || '-',
            Status: item.status || '-',
            Expression: item.expression || item.formula || item.sql || '-'
        })));

        const variableRows = (analysis.cards || []).flatMap(c => (c.variables || []).map(item => ({
            Card: c.name || c.title || '-',
            Name: item.name || '-',
            Description: item.description || '-',
            Type: item.dataType || '-',
            Expression: item.expression || '-'
        })));

        const setRows = (analysis.cards || []).flatMap(c => (c.sets || []).map(item => ({
            Card: c.name || c.title || '-',
            Name: item.name || '-',
            Type: item.dataType || '-',
            Status: item.status || '-',
            Expression: item.expression || item.formula || '-'
        })));

        const groupRows = (analysis.cards || []).flatMap(c => (c.groups || []).map(item => ({
            Card: c.name || c.title || '-',
            Name: item.name || '-',
            Type: item.dataType || '-',
            Status: item.status || '-',
            Expression: item.expression || item.formula || '-'
        })));

        const hierarchyRows = (analysis.cards || []).flatMap(c => (c.hierarchy || []).map(item => ({
            Card: c.name || c.title || '-',
            Title: item.title || '-',
            'Chart Type': item.chartType || '-',
            Type: item.type || '-',
            URN: item.urn || '-'
        })));

        const dataflowRows = (analysis.dataflows || []).map(item => ({
            Name: item.name || '-',
            ID: item.id || item.dataflowId || '-',
            Type: item.type || item.dataFlowType || '-'
        }));

        exportWorkbook([
            // { name: 'Summary', rows: summaryRows },
            { name: 'Page-wise Breakdown', rows: pageWiseRows },
            { name: 'Datasets', rows: datasetRows },
            { name: 'Cards', rows: cardRows },
            { name: 'Columns', rows: columnRows },
            { name: 'Filters', rows: filterRows },
            { name: 'Calculated Fields', rows: calcRows },
            { name: 'Variables', rows: variableRows },
            { name: 'Sets', rows: setRows },
            { name: 'Groups', rows: groupRows },
            { name: 'Hierarchy', rows: hierarchyRows }
        ], 'Domo_Discovery');
    };


    /*
    |--------------------------------------------------------------------------
    | Tab Change
    |--------------------------------------------------------------------------
    */

    const handleTabChange = (key) => {

        setActiveTab(key);

        setSearch('');

        setPage(1);
    };


    /*
    |--------------------------------------------------------------------------
    | Search Change
    |--------------------------------------------------------------------------
    */

    const handleSearch = (event) => {

        setSearch(
            event.target.value
        );

        setPage(1);
    };


    /*
    |--------------------------------------------------------------------------
    | Tabs
    |--------------------------------------------------------------------------
    */

    const tabs = [

        {
            key: 'datasets',
            label: 'Datasets',
            icon: (
                <FontAwesomeIcon
                    icon={faDatabase}
                />
            ),
            count: analysis?.summary?.datasets || 0
        },

        {
            key: 'cards',
            label: 'Cards',
            icon: (
                <FontAwesomeIcon
                    icon={faChartBar}
                />
            ),
            count: analysis?.summary?.cards || 0
        },

        {
            key: 'columns',
            label: 'Columns',
            icon: (
                <FontAwesomeIcon
                    icon={faColumns}
                />
            ),
            count: analysis?.summary?.columns || 0
        },

        {
            key: 'pages',
            label: 'Pages',
            icon: (
                <FontAwesomeIcon
                    icon={faTable}
                />
            ),
            count: analysis?.summary?.pages || 0
        },

        {
            key: 'filters',
            label: 'Filters',
            icon: (
                <FontAwesomeIcon
                    icon={faSearch}
                />
            ),
            count: analysis?.summary?.filters || 0
        },

        {
            key: 'calc',
            label: 'Calculated Fields',
            icon: (
                <FontAwesomeIcon
                    icon={faCode}
                />
            ),
            count: analysis?.summary?.calc || (analysis?.cards || []).reduce((total, c) => total + (c.calc?.length || 0), 0)
        },

        {
            key: 'variables',
            label: 'Variables',
            icon: (
                <FontAwesomeIcon
                    icon={faSquareRootVariable}
                />
            ),
            count: analysis?.summary?.variables || 0
        },

        {
            key: 'sets',
            label: 'Sets',
            icon: (
                <FontAwesomeIcon
                    icon={faObjectGroup}
                />
            ),
            count: analysis?.summary?.sets || 0
        },

        {
            key: 'groups',
            label: 'Groups',
            icon: (
                <FontAwesomeIcon
                    icon={faTags}
                />
            ),
            count: analysis?.summary?.groups_ || 0
        },

        {
            key: 'hierarchy',
            label: 'Hierarchy',
            icon: (
                <FontAwesomeIcon
                    icon={faLayerGroup}
                />
            ),
            count: analysis?.summary?.hierarchy || 0
        }
    ];


    /*
    |--------------------------------------------------------------------------
    | Render
    |--------------------------------------------------------------------------
    */

    return (
        <div
            style={{
                padding: '24px',
                color: 'var(--text-primary)'
            }}
        >

            {/* =========================================================
                HEADER
            ========================================================== */}

            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '24px'
                }}
            >

                <div>

                    <h2
                        style={{
                            fontSize: '20px',
                            fontWeight: 600,
                            marginBottom: '4px'
                        }}
                    >

                        <FontAwesomeIcon
                            icon={faMagnifyingGlassChart}
                            style={{
                                marginRight: '9px',
                                color: '#1e447e'
                            }}
                        />

                        Domo Discovery

                    </h2>

                    <p
                        style={{
                            fontSize: '13px',
                            color:
                                'var(--text-secondary)',
                            margin: 0
                        }}
                    >
                        Select a Domo dashboard or page
                        and analyze its dependencies.
                    </p>

                </div>


                {/* ACTIONS */}

                <div
                    style={{
                        display: 'flex',
                        gap: '8px'
                    }}
                >

                    <button
                        type="button"
                        onClick={
                            handleConnectDomo
                        }
                        style={{
                            border: 'none',
                            borderRadius: '6px',
                            padding: '9px 15px',
                            background: '#1e447e',
                            color: '#fff',
                            fontSize: '13px',
                            fontWeight: 500,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            cursor: 'pointer'
                        }}
                    >

                        <FontAwesomeIcon
                            icon={faPlug}
                        />

                        Connect to Domo

                    </button>


                    {analysis && (

                        <button
                            type="button"
                            onClick={
                                handleRefresh
                            }
                            disabled={isFetching}
                            style={{
                                border:
                                    '1px solid var(--border-color)',
                                borderRadius: '6px',
                                padding: '9px 14px',
                                background:
                                    'white',
                                color:
                                    'var(--text-primary)',
                                fontSize: '13px',
                                fontWeight: 500,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                cursor: 'pointer'
                            }}
                        >

                            <FontAwesomeIcon
                                icon={faRotate}
                                spin={isFetching}
                            />

                            Refresh

                        </button>

                    )}

                    {analysis && (

                        <button
                            type="button"
                            onClick={handleExportExcel}
                            style={{
                                border: '1px solid var(--border-color)',
                                borderRadius: '6px',
                                padding: '9px 14px',
                                background: 'white',
                                color: '#1e7e34',
                                fontSize: '13px',
                                fontWeight: 500,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                cursor: 'pointer'
                            }}
                        >

                            <FontAwesomeIcon
                                icon={faFileExcel}
                            />

                            Export to Excel

                        </button>

                    )}

                </div>

            </div>


            {/* =========================================================
                SELECTED RESOURCE
            ========================================================== */}

            {selectedResource && (

                <div
                    style={{
                        background:
                            'white',
                        border:
                            '1px solid var(--border-color)',
                        borderRadius: '8px',
                        padding: '14px 16px',
                        marginBottom: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent:
                            'space-between'
                    }}
                >

                    <div>

                        <div
                            style={{
                                fontSize: '11px',
                                color:
                                    'var(--text-secondary)',
                                marginBottom: '4px'
                            }}
                        >
                            ANALYZING
                        </div>

                        <div
                            style={{
                                fontSize: '14px',
                                fontWeight: 600
                            }}
                        >

                            {selectedResource
                                .map(
                                    item =>
                                        item.name ||
                                        item.title
                                )
                                .join(', ')}

                        </div>

                    </div>


                    <button
                        type="button"
                        onClick={
                            handleConnectDomo
                        }
                        style={{
                            border:
                                '1px solid var(--border-color)',
                            background:
                                'var(--card-bg)',
                            color:
                                'var(--text-primary)',
                            borderRadius: '6px',
                            padding: '7px 12px',
                            fontSize: '12px',
                            cursor: 'pointer'
                        }}
                    >
                        Change
                    </button>

                </div>

            )}


            {/* =========================================================
                INITIAL LOAD (discovery data fetching on page mount)
            ========================================================== */}

            {!analysis && isLoading && (

                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px',
                        padding: '60px 0',
                        color: 'var(--text-secondary)',
                        fontSize: '13px'
                    }}
                >
                    <FontAwesomeIcon icon={faRotate} spin />
                    Loading Domo discovery data...
                </div>

            )}


            {/* =========================================================
                EMPTY STATE
            ========================================================== */}

            {!analysis && !isLoading && (

                <EmptyDiscovery
                    onConnect={
                        handleConnectDomo
                    }
                />

            )}


            {/* =========================================================
                SUMMARY
            ========================================================== */}

            {analysis && (

                <>

                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns:
                                'repeat(auto-fit, minmax(170px, 1fr))',
                            gap: '12px',
                            marginBottom: '24px'
                        }}
                    >

                        <SummaryMetric
                            title="Datasets"
                            value={
                                analysis.summary
                                    ?.datasets || 0
                            }
                            icon={
                                <FontAwesomeIcon
                                    icon={faDatabase}
                                />
                            }
                        />

                        <SummaryMetric
                            title="Cards"
                            value={
                                analysis.summary
                                    ?.cards || 0
                            }
                            icon={
                                <FontAwesomeIcon
                                    icon={faChartBar}
                                />
                            }
                        />

                        <SummaryMetric
                            title="Columns"
                            value={
                                analysis.summary
                                    ?.columns || 0
                            }
                            icon={
                                <FontAwesomeIcon
                                    icon={faColumns}
                                />
                            }
                        />

                        <SummaryMetric
                            title="Pages"
                            value={
                                analysis.summary
                                    ?.pages || 0
                            }
                            icon={
                                <FontAwesomeIcon
                                    icon={faTable}
                                />
                            }
                        />

                        
                        <SummaryMetric
                            title="Filters"
                            value={
                                analysis.summary
                                    ?.filters || 0
                            }
                            icon={
                                <FontAwesomeIcon
                                    icon={faSearch}
                                />
                            }
                        />

                        <SummaryMetric
                            title="Calculated Fields"
                            value={
                                analysis.summary
                                    ?.calc ||
                                    (analysis.cards || []).reduce((total, c) => total + (c.calc?.length || 0), 0)
                            }
                            icon={
                                <FontAwesomeIcon
                                    icon={faCode}
                                />
                            }
                        />

                        <SummaryMetric
                            title="Variables"
                            value={
                                analysis.summary
                                    ?.variables || 0
                            }
                            icon={
                                <FontAwesomeIcon
                                    icon={faSquareRootVariable}
                                />
                            }
                        />

                        <SummaryMetric
                            title="Sets"
                            value={
                                analysis.summary
                                    ?.sets || 0
                            }
                            icon={
                                <FontAwesomeIcon
                                    icon={faMagnifyingGlassChart}
                                />
                            }
                        />

                        <SummaryMetric
                            title="Groups"
                            value={
                                analysis.summary
                                    ?.groups_ || 0
                            }
                            icon={
                                <FontAwesomeIcon
                                    icon={faTags}
                                />
                            }
                        />

                        <SummaryMetric
                            title="Hierarchy"
                            value={
                                analysis.summary
                                    ?.hierarchy || 0
                            }
                            icon={
                                <FontAwesomeIcon
                                    icon={faLayerGroup}
                                />
                            }
                        />

                    </div>


                    {/* =================================================
                        PAGE-WISE SUMMARY TABLE
                    ================================================== */}

                    {pageWiseSummary.length > 0 && (
                        <div style={{
                            background: 'white',
                            border: '1px solid var(--border-color)',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            marginBottom: '24px'
                        }}>
                            <div style={{
                                padding: '14px 16px',
                                borderBottom: '1px solid var(--border-color)',
                                fontSize: '14px',
                                fontWeight: 600
                            }}>
                                Page-wise Breakdown
                            </div>
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                                    <thead>
                                        <tr>
                                            <th style={discTableHeaderStyle}>Page Name</th>
                                            <th style={{ ...discTableHeaderStyle, textAlign: 'center' }}>Datasets</th>
                                            <th style={{ ...discTableHeaderStyle, textAlign: 'center' }}>Cards</th>
                                            <th style={{ ...discTableHeaderStyle, textAlign: 'center' }}>Columns</th>
                                            <th style={{ ...discTableHeaderStyle, textAlign: 'center' }}>Filters</th>
                                            <th style={{ ...discTableHeaderStyle, textAlign: 'center' }}>Calc. Fields</th>
                                            <th style={{ ...discTableHeaderStyle, textAlign: 'center' }}>Variables</th>
                                            <th style={{ ...discTableHeaderStyle, textAlign: 'center' }}>Sets</th>
                                            <th style={{ ...discTableHeaderStyle, textAlign: 'center' }}>Groups</th>
                                            <th style={{ ...discTableHeaderStyle, textAlign: 'center' }}>Hierarchy</th>
                                            
                                            <th style={{ ...discTableHeaderStyle, textAlign: 'center', width: '50px' }}>Details</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pageWiseSummary.map(row => (
                                            <tr key={row.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                                <td style={discTableCellStyle}>
                                                    <span style={{ fontWeight: 600 }}>{row.name}</span>
                                                </td>
                                                <td style={{ ...discTableCellStyle, textAlign: 'center' }}>{row.datasets}</td>
                                                <td style={{ ...discTableCellStyle, textAlign: 'center' }}>{row.cards}</td>
                                                <td style={{ ...discTableCellStyle, textAlign: 'center' }}>{row.columns}</td>
                                                <td style={{ ...discTableCellStyle, textAlign: 'center' }}>{row.filters}</td>
                                                <td style={{ ...discTableCellStyle, textAlign: 'center' }}>{row.calc}</td>
                                                <td style={{ ...discTableCellStyle, textAlign: 'center' }}>{row.variables}</td>
                                                <td style={{ ...discTableCellStyle, textAlign: 'center' }}>{row.sets}</td>
                                                <td style={{ ...discTableCellStyle, textAlign: 'center' }}>{row.groups}</td>
                                                <td style={{ ...discTableCellStyle, textAlign: 'center' }}>{row.hierarchy}</td>
                                                <td style={{ ...discTableCellStyle, textAlign: 'center' }}>
                                                    <button
                                                        type="button"
                                                        onClick={() => setDetailPage(row)}
                                                        title={`View details for ${row.name}`}
                                                        style={{
                                                            border: 'none', background: 'transparent',
                                                            color: '#1e447e', cursor: 'pointer',
                                                            fontSize: '14px', padding: '4px'
                                                        }}
                                                    >
                                                        <FontAwesomeIcon icon={faSearch} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                </>


            )}


            {/* =========================================================
                CONNECT MODAL
            ========================================================== */}

            {showConnectModal && (

                <DomoResourceModal
                    pages={
                        availablePages
                    }
                    selectedPages={
                        selectedPages
                    }
                    loading={
                        isFetching
                    }
                    analyzing={
                        analyzing
                    }
                    onClose={() =>
                        setShowConnectModal(
                            false
                        )
                    }
                    onToggle={
                        togglePageSelection
                    }
                    onAnalyze={
                        handleAnalyzeSelected
                    }
                />

            )}

            {/* =========================================================
                PAGE DETAIL MODAL
            ========================================================== */}

            {detailPage && (
                <PageDetailModal
                    pageRow={detailPage}
                    analysis={analysis}
                    onClose={() => setDetailPage(null)}
                />
            )}

        </div>
    );
}


/*
|--------------------------------------------------------------------------
| Empty Discovery
|--------------------------------------------------------------------------
*/

function EmptyDiscovery({
    onConnect
}) {

    return (
        <div
            style={{
                background:
                    'white',
                border:
                    '1px solid var(--border-color)',
                borderRadius: '10px',
                minHeight: '380px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center'
            }}
        >

            <div>

                <div
                    style={{
                        width: '64px',
                        height: '64px',
                        borderRadius: '14px',
                        background: '#eef3ff',
                        color: '#1e447e',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 18px',
                        fontSize: '25px'
                    }}
                >

                    <FontAwesomeIcon
                        icon={faLayerGroup}
                    />

                </div>


                <h3
                    style={{
                        fontSize: '17px',
                        marginBottom: '7px'
                    }}
                >
                    Connect to Domo
                </h3>


                <p
                    style={{
                        fontSize: '13px',
                        color:
                            'var(--text-secondary)',
                        maxWidth: '430px',
                        lineHeight: 1.6,
                        margin:
                            '0 auto 18px'
                    }}
                >
                    Connect to your Domo environment,
                    select a dashboard or page, and
                    analyze its datasets, cards,
                    columns, filters and DataFlows.
                </p>

{/* 
                <button
                    type="button"
                    onClick={onConnect}
                    style={{
                        border: 'none',
                        borderRadius: '6px',
                        padding: '10px 18px',
                        background: '#1e447e',
                        color: '#fff',
                        fontSize: '13px',
                        fontWeight: 500,
                        cursor: 'pointer'
                    }}
                >

                    <FontAwesomeIcon
                        icon={faPlug}
                        style={{
                            marginRight: '7px'
                        }}
                    />

                    Connect to Domo

                </button> */}

            </div>

        </div>
    );
}


/*
|--------------------------------------------------------------------------
| Domo Resource Modal
|--------------------------------------------------------------------------
*/

function DomoResourceModal({
    pages,
    selectedPages,
    loading,
    analyzing,
    onClose,
    onToggle,
    onAnalyze
}) {

    const [search, setSearch] =
        useState('');


    const filteredPages =
        pages.filter(item => {

            if (!search.trim()) {
                return true;
            }

            const value =
                search.toLowerCase();

            return (
                String(
                    item.name ||
                    item.title ||
                    ''
                )
                    .toLowerCase()
                    .includes(value)
                ||
                String(
                    item.id || ''
                )
                    .toLowerCase()
                    .includes(value)
            );

        });


    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                background:
                    'rgba(0, 0, 0, 0.45)',
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px'
            }}
        >

            <div
                style={{
                    width: '720px',
                    maxWidth: '100%',
                    maxHeight: '85vh',
                    background:
                        'white',
                    borderRadius: '10px',
                    border:
                        '1px solid var(--border-color)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    boxShadow:
                        '0 12px 40px rgba(0,0,0,.18)'
                }}
            >

                {/* HEADER */}

                <div
                    style={{
                        padding: '18px 20px',
                        borderBottom:
                            '1px solid var(--border-color)',
                        display: 'flex',
                        justifyContent:
                            'space-between',
                        alignItems: 'center'
                    }}
                >

                    <div>

                        <div
                            style={{
                                fontSize: '16px',
                                fontWeight: 600
                            }}
                        >

                            Select Domo Dashboard / Page

                        </div>

                        <div
                            style={{
                                fontSize: '12px',
                                color:
                                    'var(--text-secondary)',
                                marginTop: '4px'
                            }}
                        >
                            Select one or more resources to analyze.

                        </div>


                    </div>


                    <button
                        type="button"
                        onClick={onClose}
                        style={{
                            width: '30px',
                            height: '30px',
                            border: 'none',
                            background:
                                'transparent',
                            color:
                                'var(--text-secondary)',
                            cursor: 'pointer',
                            fontSize: '16px'
                        }}
                    >

                        <FontAwesomeIcon
                            icon={faXmark}
                        />

                    </button>

                </div>


                {/* SEARCH */}

                <div
                    style={{
                        padding: '14px 16px',
                        borderBottom:
                            '1px solid var(--border-color)'
                    }}
                >

                    <div
                        style={{
                            position:
                                'relative'
                        }}
                    >

                        <FontAwesomeIcon
                            icon={faSearch}
                            style={{
                                position:
                                    'absolute',
                                left: '12px',
                                top: '50%',
                                transform:
                                    'translateY(-50%)',
                                color:
                                    'var(--text-tertiary)',
                                fontSize: '13px'
                            }}
                        />

                        <input
                            type="text"
                            value={search}
                            onChange={
                                e =>
                                    setSearch(
                                        e.target.value
                                    )
                            }
                            placeholder="Search dashboards or pages..."
                            style={{
                                width: '100%',
                                height: '36px',
                                border:
                                    '1px solid var(--border-color)',
                                borderRadius: '6px',
                                padding:
                                    '0 12px 0 34px',
                                outline: 'none',
                                background:
                                    'var(--input-bg)',
                                color:
                                    'var(--text-primary)',
                                fontSize: '13px'
                            }}
                        />

                    </div>

                </div>


                {/* BODY */}

                <div
                    style={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: '10px 12px'
                    }}
                >

                    {loading ? (

                        <Loading
                            text="Loading Domo dashboards and pages..."
                        />

                    ) : !filteredPages.length ? (

                        <div
                            style={{
                                textAlign: 'center',
                                padding: '50px 20px',
                                color:
                                    'var(--text-tertiary)',
                                fontSize: '13px'
                            }}
                        >
                            No dashboards or pages found.
                        </div>

                    ) : (

                        filteredPages.map(
                            item => {

                                const selected =
                                    selectedPages.some(
                                        selectedItem =>
                                            String(
                                                selectedItem.id
                                            ) ===
                                            String(
                                                item.id
                                            )
                                    );


                                return (
                                    <button
                                        key={
                                            item.id
                                        }
                                        type="button"
                                        onClick={() =>
                                            onToggle(
                                                item
                                            )
                                        }
                                        style={{
                                            width: '100%',
                                            border:
                                                selected
                                                    ? '1px solid #1e447e'
                                                    : '1px solid var(--border-color)',
                                            background:
                                                selected
                                                    ? '#eef3ff'
                                                    : 'white',
                                            borderRadius:
                                                '7px',
                                            padding:
                                                '12px',
                                            marginBottom:
                                                '8px',
                                            display:
                                                'flex',
                                            alignItems:
                                                'center',
                                            textAlign:
                                                'left',
                                            cursor:
                                                'pointer'
                                        }}
                                    >

                                        <div
                                            style={{
                                                width:
                                                    '34px',
                                                height:
                                                    '34px',
                                                borderRadius:
                                                    '7px',
                                                background:
                                                    selected
                                                        ? '#1e447e'
                                                        : 'var(--hover-bg)',
                                                color:
                                                    selected
                                                        ? '#fff'
                                                        : '#1e447e',
                                                display:
                                                    'flex',
                                                alignItems:
                                                    'center',
                                                justifyContent:
                                                    'center',
                                                marginRight:
                                                    '11px',
                                                flexShrink:
                                                    0
                                            }}
                                        >

                                            <FontAwesomeIcon
                                                icon={
                                                    faTable
                                                }
                                            />

                                        </div>


                                        <div
                                            style={{
                                                flex: 1,
                                                minWidth:
                                                    0
                                            }}
                                        >

                                            <div
                                                style={{
                                                    fontSize:
                                                        '13px',
                                                    fontWeight:
                                                        600,
                                                    color:
                                                        'var(--text-primary)',
                                                    overflow:
                                                        'hidden',
                                                    textOverflow:
                                                        'ellipsis',
                                                    whiteSpace:
                                                        'nowrap'
                                                }}
                                            >

                                                {item.name ||
                                                    item.title ||
                                                    'Unnamed Page'}

                                            </div>


                                            <div
                                                style={{
                                                    fontSize:
                                                        '11px',
                                                    color:
                                                        'var(--text-secondary)',
                                                    marginTop:
                                                        '3px'
                                                }}
                                            >

                                                ID: {
                                                    item.id ||
                                                    '-'
                                                }

                                                {item.cards && (
                                                    <>
                                                        {' â€¢ '}
                                                        {
                                                            item.cards.length
                                                        } cards
                                                    </>
                                                )}

                                            </div>

                                        </div>


                                        <div
                                            style={{
                                                width:
                                                    '24px',
                                                height:
                                                    '24px',
                                                borderRadius:
                                                    '50%',
                                                border:
                                                    selected
                                                        ? 'none'
                                                        : '1px solid var(--border-color)',
                                                background:
                                                    selected
                                                        ? '#1e447e'
                                                        : 'transparent',
                                                color:
                                                    '#fff',
                                                display:
                                                    'flex',
                                                alignItems:
                                                    'center',
                                                justifyContent:
                                                    'center'
                                            }}
                                        >

                                            {selected && (
                                                <FontAwesomeIcon
                                                    icon={
                                                        faCheck
                                                    }
                                                    style={{
                                                        fontSize:
                                                            '11px'
                                                    }}
                                                />
                                            )}

                                        </div>

                                    </button>
                                );

                            }
                        )

                    )}

                </div>


                {/* FOOTER */}

                <div
                    style={{
                        padding: '13px 16px',
                        borderTop:
                            '1px solid var(--border-color)',
                        display: 'flex',
                        justifyContent:
                            'space-between',
                        alignItems: 'center'
                    }}
                >

                    <div
                        style={{
                            fontSize: '12px',
                            color:
                                'var(--text-secondary)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px'
                        }}
                    >
                        <span>
                            {selectedPages.length}
                            {' '}
                            resource
                            {selectedPages.length === 1
                                ? ''
                                : 's'} selected
                        </span>
                        <button
                            type="button"
                            onClick={() => {
                                if (selectedPages.length === pages.length) {
                                    setSelectedPages([]);
                                    setSelectedResources([]);
                                    setSelectedResource(null);
                                } else {
                                    setSelectedPages([...pages]);
                                    setSelectedResources([...pages]);
                                    setSelectedResource([...pages]);
                                }
                            }}
                            style={{
                                border: 'none', background: 'transparent',
                                color: '#1e447e', fontSize: '11px',
                                cursor: 'pointer', textDecoration: 'underline', padding: 0
                            }}
                        >
                            {selectedPages.length === pages.length ? 'Deselect All' : 'Select All'}
                        </button>
                    </div>


                    <div
                        style={{
                            display: 'flex',
                            gap: '8px'
                        }}
                    >

                        <button
                            type="button"
                            onClick={onClose}
                            style={{
                                border:
                                    '1px solid var(--border-color)',
                                borderRadius: '6px',
                                padding:
                                    '8px 14px',
                                background:
                                    'var(--card-bg)',
                                color:
                                    'var(--text-primary)',
                                fontSize: '12px',
                                cursor: 'pointer'
                            }}
                        >
                            Cancel
                        </button>


                        <button
                            type="button"
                            disabled={
                                !selectedPages.length ||
                                analyzing
                            }
                            onClick={
                                onAnalyze
                            }
                            style={{
                                border: 'none',
                                borderRadius: '6px',
                                padding:
                                    '8px 15px',
                                background:
                                    '#1e447e',
                                color: '#fff',
                                fontSize: '12px',
                                fontWeight: 500,
                                cursor:
                                    !selectedPages.length ||
                                        analyzing
                                        ? 'not-allowed'
                                        : 'pointer',
                                opacity:
                                    !selectedPages.length ||
                                        analyzing
                                        ? 0.6
                                        : 1
                            }}
                        >

                            <FontAwesomeIcon
                                icon={
                                    faMagnifyingGlassChart
                                }
                                style={{
                                    marginRight:
                                        '6px'
                                }}
                            />

                            {analyzing
                                ? 'Analyzing...'
                                : 'Analyze Selected'}

                        </button>

                    </div>

                </div>

            </div>

        </div>
    );
}


/*
|--------------------------------------------------------------------------
| Summary Metric
|--------------------------------------------------------------------------
*/

function SummaryMetric({
    title,
    value,
    icon
}) {

    return (
        <div
            style={{
                background:
                    'white',
                border:
                    '1px solid var(--border-color)',
                borderRadius: '8px',
                padding: '16px',
                display: 'flex',
                alignItems: 'center',
                minHeight: '82px'
            }}
        >

            <div
                style={{
                    width: '42px',
                    height: '42px',
                    borderRadius: '8px',
                    background: '#eef3ff',
                    color: '#1e447e',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '17px',
                    marginRight: '12px',
                    flexShrink: 0
                }}
            >
                {icon}
            </div>


            <div>

                <div
                    style={{
                        fontSize: '11px',
                        color:
                            'var(--text-secondary)',
                        marginBottom: '3px'
                    }}
                >
                    {title}
                </div>

                <div
                    style={{
                        fontSize: '22px',
                        fontWeight: 600
                    }}
                >
                    {value || 0}
                </div>

            </div>

        </div>
    );
}


/*
|--------------------------------------------------------------------------
| Discovery Table
|--------------------------------------------------------------------------
*/

function DiscoveryTable({
    type,
    data
}) {

    if (!data?.length) {

        return (
            <div
                style={{
                    textAlign: 'center',
                    padding: '48px 20px',
                    color:
                        'var(--text-tertiary)',
                    fontSize: '13px'
                }}
            >
                No {type} found.
            </div>
        );
    }


    switch (type) {

        case 'datasets':
            return (
                <DatasetTable
                    data={data}
                />
            );

        case 'cards':
            return (
                <CardTable
                    data={data}
                />
            );

        case 'columns':
            return (
                <ColumnTable
                    data={data}
                />
            );

        case 'pages':
            return (
                <PageTable
                    data={data}
                />
            );

        case 'dataflows':
            return (
                <DataflowTable
                    data={data}
                />
            );

        case 'beastmodes':
            return (
                <BeastModeTable
                    data={data}
                />
            );

        default:
            return null;
    }
}


/*
|--------------------------------------------------------------------------
| Dataset Table
|--------------------------------------------------------------------------
*/

function DatasetTable({
    data
}) {

    return (
        <DiscoverySimpleTable
            headers={[
                'Name',
                'Dataset ID',
                'Rows',
                'Columns',
                'Last Updated'
            ]}
            rows={
                data.map(item => [

                    item.schema.name ||
                    item.displayName ||
                    '-',

                    item.schema.id ||
                    item.datasetId ||
                    '-',

                    formatNumber(
                        item.schema.rows ??
                        item.rowCount
                    ),

                    item.schema.columns ??
                    item.columnCount ??
                    0,

                    item.schema.lastUpdated ||
                    item.schema.updatedAt ||
                    '-'
                ])
            }
        />
    );
}


/*
|--------------------------------------------------------------------------
| Card Table
|--------------------------------------------------------------------------
*/

function CardTable({
    data
}) {

    return (
        <DiscoverySimpleTable
            headers={[
                'Name',
                'Card ID',
                'Dataset',
                'Chart Type',
                "Page",
                "Calculated Fields"
            ]}
            rows={
                data.map(item => [

                    item.name ||
                    item.title ||
                    '-',

                    item.id ||
                    item.cardId ||
                    '-',

                    item.datasetName ||
                    item.datasetId ||
                    '-',

                    item.chartType ||
                    item.cardType ||
                    '-',

                    item.pageName ||
                    item.pageId ||
                    '-',

                    item.calculatedFields?.length ??
                    item.calculatedFieldCount ??
                    0
                ])
            }
        />
    );
}


/*
|--------------------------------------------------------------------------
| Column Table
|--------------------------------------------------------------------------
*/

function ColumnTable({
    data
}) {

    return (
        <DiscoverySimpleTable
            headers={[
                'Column',
                'Type'
            ]}
            rows={
                data.map(item => [

                    item.name ||
                    item.columnName ||
                    '-',

                    item.type ||
                    item.dataType ||
                    '-'
                ])
            }
        />
    );
}


/*
|--------------------------------------------------------------------------
| Page Table
|--------------------------------------------------------------------------
*/

function PageTable({
    data
}) {

    return (
        <DiscoverySimpleTable
            headers={[
                'Name',
                'Page ID',
                'Cards',
                'Description'
            ]}
            rows={
                data.map(item => [

                    item.pageName ||
                    item.title ||
                    '-',

                    item.id ||
                    item.pageId ||
                    '-',

                    item.cards?.length ??
                    item.cardCount ??
                    0,

                    item.description ||
                    '-'
                ])
            }
        />
    );
}


/*
|--------------------------------------------------------------------------
| DataFlow Table
|--------------------------------------------------------------------------
*/

function DataflowTable({
    data
}) {

    return (
        <DiscoverySimpleTable
            headers={[
                'Name',
                'DataFlow ID',
                'Inputs',
                'Outputs',
                'Last Updated'
            ]}
            rows={
                data.map(item => [

                    item.name ||
                    item.title ||
                    '-',

                    item.id ||
                    item.dataflowId ||
                    '-',

                    item.inputs?.length ??
                    item.inputCount ??
                    0,

                    item.outputs?.length ??
                    item.outputCount ??
                    0,

                    item.lastUpdated ||
                    item.updatedAt ||
                    '-'
                ])
            }
        />
    );
}


/*
|--------------------------------------------------------------------------
| Beast Mode Table
|--------------------------------------------------------------------------
*/

function BeastModeTable({
    data
}) {
    return (
        <DiscoverySimpleTable
            headers={[
                'Name',
                'ID',
                'Dataset'
            ]}
            rows={
                data.map(item => [

                    item.name ||
                    item.title ||
                    '-',

                    item.id ||
                    item.beastModeId ||
                    '-',

                    item.datasetName ||
                    item.datasetId ||
                    '-'
                ])
            }
        />
    );
}


/*
|--------------------------------------------------------------------------
| Generic Table
|--------------------------------------------------------------------------
*/

function DiscoverySimpleTable({
    headers,
    rows
}) {

    return (
        <div
            style={{
                overflowX: 'auto'
            }}
        >

            <table
                style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '12px'
                }}
            >

                <thead>

                    <tr>

                        {headers.map(
                            header => (

                                <th
                                    key={header}
                                    style={{
                                        textAlign: 'left',
                                        padding:
                                            '11px 14px',
                                        borderBottom:
                                            '1px solid var(--border-color)',
                                        color:
                                            'var(--text-secondary)',
                                        fontWeight: 600,
                                        whiteSpace:
                                            'nowrap',
                                        background:
                                            'var(--hover-bg)'
                                    }}
                                >
                                    {header}
                                </th>

                            )
                        )}

                    </tr>

                </thead>


                <tbody>

                    {rows.map(
                        (row, rowIndex) => (

                            <tr
                                key={rowIndex}
                                style={{
                                    borderBottom:
                                        '1px solid var(--border-color)'
                                }}
                            >

                                {row.map(
                                    (
                                        value,
                                        columnIndex
                                    ) => (

                                        <td
                                            key={
                                                columnIndex
                                            }
                                            style={{
                                                padding:
                                                    '11px 14px',
                                                color:
                                                    'var(--text-primary)',
                                                verticalAlign:
                                                    'middle',
                                                maxWidth:
                                                    '350px',
                                                overflow:
                                                    'hidden',
                                                textOverflow:
                                                    'ellipsis',
                                                whiteSpace:
                                                    'nowrap'
                                            }}
                                            title={
                                                typeof value ===
                                                    'string'
                                                    ? value
                                                    : undefined
                                            }
                                        >

                                            {columnIndex === 1 &&
                                                value &&
                                                value !== '-' ? (

                                                <code
                                                    style={{
                                                        fontSize:
                                                            '11px'
                                                    }}
                                                >
                                                    {value}
                                                </code>

                                            ) : (
                                                value
                                            )}

                                        </td>

                                    )
                                )}

                            </tr>

                        )
                    )}

                </tbody>

            </table>

        </div>
    );
}


/*
|--------------------------------------------------------------------------
| Pagination
|--------------------------------------------------------------------------
*/

function Pagination({
    page,
    totalPages,
    onPageChange,
    total,
    pageSize
}) {

    const start =
        ((page - 1) * pageSize) + 1;

    const end =
        Math.min(
            page * pageSize,
            total
        );


    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px 16px',
                borderTop:
                    '1px solid var(--border-color)'
            }}
        >

            <div
                style={{
                    fontSize: '12px',
                    color:
                        'var(--text-secondary)'
                }}
            >
                Showing {start} - {end} of {total}
            </div>


            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                }}
            >

                <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() =>
                        onPageChange(
                            page - 1
                        )
                    }
                    style={{
                        width: '32px',
                        height: '30px',
                        border:
                            '1px solid var(--border-color)',
                        background:
                            'var(--card-bg)',
                        color:
                            'var(--text-primary)',
                        borderRadius: '5px',
                        cursor:
                            page <= 1
                                ? 'not-allowed'
                                : 'pointer',
                        opacity:
                            page <= 1
                                ? 0.5
                                : 1
                    }}
                >
                    <FontAwesomeIcon
                        icon={faChevronLeft}
                    />
                </button>


                <span
                    style={{
                        fontSize: '12px',
                        padding: '0 8px',
                        color:
                            'var(--text-secondary)'
                    }}
                >
                    Page {page} of {totalPages}
                </span>


                <button
                    type="button"
                    disabled={
                        page >= totalPages
                    }
                    onClick={() =>
                        onPageChange(
                            page + 1
                        )
                    }
                    style={{
                        width: '32px',
                        height: '30px',
                        border:
                            '1px solid var(--border-color)',
                        background:
                            'var(--card-bg)',
                        color:
                            'var(--text-primary)',
                        borderRadius: '5px',
                        cursor:
                            page >= totalPages
                                ? 'not-allowed'
                                : 'pointer',
                        opacity:
                            page >= totalPages
                                ? 0.5
                                : 1
                    }}
                >
                    <FontAwesomeIcon
                        icon={faChevronRight}
                    />
                </button>

            </div>

        </div>
    );
}


/*
|--------------------------------------------------------------------------
| Loading
|--------------------------------------------------------------------------
*/

function Loading({
    text = 'Discovering Domo resources...'
}) {

    return (
        <div
            style={{
                textAlign: 'center',
                padding: '60px 20px',
                color:
                    'var(--text-secondary)'
            }}
        >

            <div
                style={{
                    width: '30px',
                    height: '30px',
                    border:
                        '3px solid var(--border-color)',
                    borderTopColor:
                        '#1e447e',
                    borderRadius: '50%',
                    margin:
                        '0 auto 14px',
                    animation:
                        'domoDiscoverySpin 0.8s linear infinite'
                }}
            />

            <div
                style={{
                    fontSize: '13px'
                }}
            >
                {text}
            </div>


            <style>
                {`
                    @keyframes domoDiscoverySpin {
                        from {
                            transform: rotate(0deg);
                        }

                        to {
                            transform: rotate(360deg);
                        }
                    }
                `}
            </style>

        </div>
    );
}


/*
|--------------------------------------------------------------------------
| Helpers
|--------------------------------------------------------------------------
*/

function formatNumber(value) {

    if (
        value === null ||
        value === undefined ||
        value === ''
    ) {
        return '-';
    }

    if (
        typeof value === 'number'
    ) {
        return value.toLocaleString();
    }

    return value;
}


/*
|--------------------------------------------------------------------------
| Page Detail Modal
|--------------------------------------------------------------------------
| Shows the full detail of a single page â€” datasets, cards, columns,
| beast modes, dataflows â€” in a tabbed popup.
*/

function PageDetailModal({ pageRow, analysis, onClose }) {
    const [tab, setTab] = useState('cards');

    const pgId = String(pageRow.id || '');
    const cards = (analysis?.cards || []).filter(c =>
        String(c.pageId || c.page_id || c.page || '') === pgId
    );

    const pgDatasetIds = new Set(
        cards.map(c =>
            String(c.datasetId || c.dataSetId || c.datasourceId || c.detail?.datasourceId || c.detail?.dataSetId || '')
        ).filter(Boolean)
    );

    const datasets = (analysis?.datasets || []).filter(d => {
        const dId = String(d.id || d.datasetId || d.dataSetId || '');
        return pgDatasetIds.has(dId);
    });

    // If no datasets matched via card references, try matching all datasets
    // that are directly associated with this page
    const finalDatasets = datasets.length ? datasets : (analysis?.datasets || []).filter(d => {
        const dPages = d.pages || [];
        return dPages.some(p => String(p.id || p.pageId || p) === pgId);
    });

    const columns = (analysis?.columns || []).filter(c =>
        pgDatasetIds.has(String(c.datasetId || ''))
    ).sort((a, b) => {
        const nameA = (a.datasetName || a.datasetId || '').toLowerCase();
        const nameB = (b.datasetName || b.datasetId || '').toLowerCase();
        return nameA.localeCompare(nameB);
    });

    const beastmodes = (analysis?.beastmodes || []).filter(b =>
        pgDatasetIds.has(String(b.datasetId || b.dataset || ''))
    );

    const dataflows = (analysis?.dataflows || []).filter(df => {
        const inputs = df.inputs || df.inputDatasets || [];
        const outputs = df.outputs || df.outputDatasets || [];
        const all = [...inputs, ...outputs, df.datasetId].filter(Boolean).map(String);
        return all.some(id => pgDatasetIds.has(id));
    });

    const filters = (analysis?.filters || []).filter(f =>
        String(f.pageId || '') === pgId
    );

    // Calculated fields / variables / sets / groups / hierarchy come
    // directly from each card (enriched by the backend).
    const calc = cards.flatMap(c => c.calc || []);
    const variables = cards.flatMap(c => c.variables || []);
    const sets = cards.flatMap(c => c.sets || []);
    const groups = cards.flatMap(c => c.groups || []);
    const hierarchy = cards.flatMap(c => c.hierarchy || []);

    const tabs = [
        { key: 'cards', label: 'Cards', count: cards.length },
        { key: 'datasets', label: 'Datasets', count: finalDatasets.length },
        { key: 'columns', label: 'Columns', count: columns.length },
        { key: 'filters', label: 'Filters', count: filters.length },
        { key: 'calc', label: 'Calc. Fields', count: calc.length },
        { key: 'variables', label: 'Variables', count: variables.length },
        { key: 'sets', label: 'Sets', count: sets.length },
        { key: 'groups', label: 'Groups', count: groups.length },
        { key: 'hierarchy', label: 'Hierarchy', count: hierarchy.length },
        
    ];

    const tabData = { cards, datasets: finalDatasets, columns, filters, calc, variables, sets, groups, hierarchy, dataflows };
    const activeData = tabData[tab] || [];

    return (
        <div
            onClick={e => { if (e.target === e.currentTarget) onClose(); }}
            style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
                zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '20px'
            }}
        >
            <div style={{
                width: 'min(1100px, 100%)', maxHeight: '88vh',
                background: 'white', border: '1px solid var(--border-color)',
                borderRadius: '10px', overflow: 'hidden',
                boxShadow: '0 15px 50px rgba(0,0,0,0.2)',
                display: 'flex', flexDirection: 'column'
            }}>
                {/* Header */}
                <div style={{
                    padding: '16px 20px', borderBottom: '1px solid var(--border-color)',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                }}>
                    <div>
                        <div style={{ fontSize: '16px', fontWeight: 600 }}>{pageRow.name}</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '3px' }}>
                            {pageRow.datasets} Datasets - {pageRow.cards} Cards - {pageRow.columns} Columns - {pageRow.filters} Filters - {pageRow.calc} Calc. Fields - {pageRow.variables} Variables - {pageRow.sets} Sets - {pageRow.groups} Groups - {pageRow.hierarchy} Hierarchy
                        </div>
                    </div>
                    <button type="button" onClick={onClose} style={{
                        width: 30, height: 30, border: 'none', background: 'var(--hover-bg)',
                        borderRadius: 6, cursor: 'pointer', fontSize: 16, color: 'var(--text-secondary)'
                    }}>
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                </div>

                {/* Tabs */}
                <div style={{ display: 'flex', borderBottom: '1px solid var(--border-color)' }}>
                    {tabs.map(t => (
                        <button
                            key={t.key}
                            type="button"
                            onClick={() => setTab(t.key)}
                            style={{
                                border: 'none',
                                borderBottom: tab === t.key ? '2px solid #1e447e' : '2px solid transparent',
                                background: tab === t.key ? 'var(--hover-bg)' : 'transparent',
                                color: tab === t.key ? '#1e447e' : 'var(--text-secondary)',
                                padding: '11px 14px', fontSize: '12px',
                                fontWeight: tab === t.key ? 600 : 400,
                                cursor: 'pointer', whiteSpace: 'nowrap'
                            }}
                        >
                            {t.label} <span style={{
                                background: 'var(--hover-bg)', borderRadius: 10,
                                padding: '2px 6px', fontSize: 10, fontWeight: 600, marginLeft: 4
                            }}>{t.count}</span>
                        </button>
                    ))}
                </div>

                {/* Content */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
                    {activeData.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-tertiary)', fontSize: '13px' }}>
                            No {tab} found for this page.
                        </div>
                    ) : (
                        <div style={{ border: '1px solid var(--border-color)', borderRadius: '8px', overflow: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                                <thead>
                                    <tr>
                                        {getDetailColumns(tab).map(col => (
                                            <th key={col} style={discTableHeaderStyle}>{col}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {activeData.slice(0, 200).map((item, idx) => (
                                        <tr key={idx} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                            {getDetailRow(tab, item).map((cell, ci) => (
                                                <td key={ci} style={discTableCellStyle}>{cell}</td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {activeData.length > 200 && (
                                <div style={{ padding: '10px', textAlign: 'center', fontSize: '11px', color: 'var(--text-secondary)' }}>
                                    Showing first 200 of {activeData.length} items.
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function getDetailColumns(tab) {
    switch (tab) {
        case 'cards': return ['Name', 'Chart Type', 'Dataset ID'];
        case 'datasets': return ['Name', 'ID', 'Columns', 'Rows'];
        case 'columns': return ['Dataset', 'Column Name', 'Data Type'];
        case 'beastmodes': return ['Name', 'Formula', 'Dataset'];
        case 'filters': return ['Column', 'Operator', 'Values', 'Card'];
        case 'calc': return ['Name', 'Type', 'Status', 'Expression'];
        case 'variables': return ['Name', 'Description', 'Type', 'Expression'];
        case 'sets': return ['Name', 'Type', 'Status', 'Expression'];
        case 'groups': return ['Name', 'Type', 'Status', 'Expression'];
        case 'hierarchy': return ['Title', 'Chart Type', 'Type', 'URN'];
        case 'dataflows': return ['Name', 'ID', 'Type'];
        default: return ['Name'];
    }
}

function getDetailRow(tab, item) {
    switch (tab) {
        case 'cards':
            return [
                item.name || item.title || '-',
                item.chartType || item.detail?.chartType || '-',
                item.datasetId || item.dataSetId || item.detail?.datasourceId || '-'
            ];
        case 'datasets':
            return [
                item.name || item.datasetName || item.displayName || '-',
                item.id || item.datasetId || '-',
                Array.isArray(item.schema) ? item.schema.length : (item.columns?.length || item.columnCount || item.schema?.columns?.length || '-'),
                item.rows || item.rowCount || item.dataRows || '-'
            ];
        case 'columns':
            return [
                item.datasetName || item.datasetId || '-',
                item.name || item.columnName || '-',
                item.type || item.dataType || item.columnType || '-'
            ];
        case 'beastmodes':
            return [
                item.name || item.functionName || item.alias || '-',
                (item.formula || item.expression || item.body || '-').slice(0, 80),
                item.datasetName || item.datasetId || '-'
            ];
        case 'filters':
            return [
                item.column || '-',
                item.operator || item.operand || '-',
                Array.isArray(item.values) ? item.values.join(', ').slice(0, 60) : (item.values || '-'),
                item.cardName || '-'
            ];
        case 'calc':
        case 'sets':
        case 'groups': {
            const expression =
                item.expression || item.formula || item.sql || '';
            return [
                item.name || '-',
                item.dataType || '-',
                item.status || '-',
                expression
                    ? (typeof expression === 'string' ? expression.slice(0, 80) : JSON.stringify(expression).slice(0, 80))
                    : '-'
            ];
        }
        case 'variables':
            return [
                item.name || '-',
                item.description || '-',
                item.dataType || '-',
                item.expression || '-'
            ];
        case 'hierarchy':
            return [
                item.title || '-',
                item.chartType || '-',
                item.type || '-',
                item.urn || '-'
            ];
        case 'dataflows':
            return [
                item.name || '-',
                item.id || item.dataflowId || '-',
                item.type || item.dataFlowType || '-'
            ];
        default:
            return [item.name || JSON.stringify(item).slice(0, 60)];
    }
}


/*
|--------------------------------------------------------------------------
| Table Styles (Discovery)
|--------------------------------------------------------------------------
*/

const discTableHeaderStyle = {
    padding: '10px 12px',
    textAlign: 'left',
    fontSize: '10px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: 'var(--text-secondary)',
    background: 'var(--hover-bg)',
    borderBottom: '1px solid var(--border-color)',
    whiteSpace: 'nowrap'
};

const discTableCellStyle = {
    padding: '9px 12px',
    fontSize: '12px',
    color: 'var(--text-primary)',
    verticalAlign: 'top'
};
